import React, { useState, useEffect, useContext, ReactNode } from 'react'
import { ContentLoading } from '@ui'
import {
  IAssignmentPlan,
  IUsageType,
  Role,
  IDepartment,
  ILocationHealthCheck,
  IDepartmentTeams,
  ISpaceType,
} from '@api/types'

import { IIntegration } from '@api/integration/types'
import { IntegrationFragment } from '@api/integration/fragments'
import { AssignmentPlanFragment, UsageTypeFragment } from '@api/fragments'
import { DocumentNode } from 'graphql'
import gql from 'graphql-tag'
import { client } from '@stores'
import { ApolloError } from 'apollo-boost'
import { useShouldReload } from '@hooks/reloadContext'
import { useUser } from '@hooks'

interface ICompanyAsset {
  fileName: string | null
  id: string
  updatedAt: string | null
  url: string | null
}

interface IAppData {
  id: string
  name: string
  fromEmail: string
  assets: ICompanyAsset[]
  product: {
    features: string[]
    integrations: string[]
  }
  integrations: {
    active: IIntegration[]
  }
  primaryAssignmentPlan: IAssignmentPlan
  types: { usageType: IUsageType[]; spaceType: ISpaceType[] }
  departments: IDepartmentTeams[]
  locationHealthCheckGroups: ILocationHealthCheck[]
}

const initState: IAppData = {
  id: '',
  name: '',
  fromEmail: '',
  assets: [],
  product: {
    features: [],
    integrations: [],
  },
  integrations: {
    active: [],
  },
  primaryAssignmentPlan: {
    id: '',
    name: '',
    alias: '',
    apType: 'master',
    createdAt: new Date(),
    deployAt: new Date(),
    createdBy: {
      name: '',
    },
    parentAssignmentPlanId: '',
  },
  types: {
    usageType: [],
    spaceType: [],
  },
  departments: [],
  locationHealthCheckGroups: [],
}

const AppDataContext = React.createContext<IAppData>(initState)

export function AppDataProvider(props: { children: ReactNode }) {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState<IAppData>(initState)
  const { user } = useUser()
  const shouldReload = useShouldReload()
  const userRole = user?.employee.role

  useEffect(() => {
    fetchAppData(userRole)
      .then((appData) => {
        setData(appData)
        setLoading(false)
      })
      .catch((error: ApolloError) => {
        window.alert(error.message)
        window.location.reload()
      })
  }, [shouldReload.appData, userRole])

  return loading ? (
    <ContentLoading />
  ) : (
    <AppDataContext.Provider {...props} value={data as IAppData} />
  )
}

export function useAppData() {
  return useContext(AppDataContext)
}

interface Payload {
  data: {
    viewer: {
      company: IAppData
    }
  }
}
export const appDataAdminQuery = gql`
  query AppData {
    viewer {
      company {
        id
        name
        fromEmail
        assets {
          fileName
          id
          updatedAt
          url(style: half)
        }
        product {
          features
          integrations
        }
        integrations {
          active {
            ...IntegrationFragment
          }
        }
        primaryAssignmentPlan {
          ...AssignmentPlanFragment
        }
        types {
          usageType {
            ...UsageTypeFragment
          }
          spaceType {
            id
            name
            displayName
          }
        }
        departments {
          id
          name
          color
          teams {
            id
            name
            color
          }
        }
        locationHealthCheckGroups {
          id
          healthScreeningConfirmButtonText
          healthScreeningMessage
          followUpQuestionEnabled
          followUpQuestionRecipient
          followUpQuestionText
          healthScreeningOverride
        }
      }
    }
  }
  ${IntegrationFragment}
  ${AssignmentPlanFragment}
  ${UsageTypeFragment}
`

export const appDataQuery = gql`
  query AppData {
    viewer {
      company {
        id
        name
        fromEmail
        assets {
          fileName
          id
          updatedAt
          url(style: half)
        }
        product {
          features
          integrations
        }
        primaryAssignmentPlan {
          ...AssignmentPlanFragment
        }
        types {
          usageType {
            ...UsageTypeFragment
          }
          spaceType {
            id
            name
            displayName
          }
        }
      }
    }
  }
  ${AssignmentPlanFragment}
  ${UsageTypeFragment}
`

type RoleAppDataQueryMap = { [key in Role]: DocumentNode }

const roleAppDataQueryMap: RoleAppDataQueryMap = {
  [Role.ADMIN]: appDataAdminQuery,
  [Role.DEPARTMENT_ADMIN]: appDataQuery,
  [Role.LOCATION_ADMIN]: appDataQuery,
  [Role.IT]: appDataQuery,
  [Role.VIEWER]: appDataQuery,
  [Role.POWER_ADMIN]: appDataQuery,
  [Role.SPACE_CAPTAIN]: appDataQuery,
  [Role.BOOKINGS_MANAGER]: appDataQuery,
}

const getAppDataQueryByRole = (role?: Role): DocumentNode =>
  !role || !roleAppDataQueryMap[role] ? appDataAdminQuery : roleAppDataQueryMap[role]

function fetchAppData(role?: Role): Promise<IAppData> {
  return client.gql.query({ query: getAppDataQueryByRole(role) }).then(
    ({
      data: {
        viewer: { company },
      },
    }: Payload) => company
  )
}
